import { memo } from 'react';
import { Box, Flex, HStack, Image, Text, VStack } from '@chakra-ui/react';
import { typeaheadCardArtImgProps } from '../utils/sanityCardArtUrl';

/** Card hit returned by `/api/suggest` (and `didYouMean` on `/api/cards`). */
export interface CardSuggestion {
  id: string;
  name: string;
  thumbnailUrl?: string | null;
  cardType?: string | null;
  region?: string | null;
  rarity?: string | null;
}

export interface CardSearchSuggestionRowProps {
  suggestion: CardSuggestion;
  isActive?: boolean;
  onPointerEnter?: () => void;
  onClick?: () => void;
  /**
   * When false, omit the bottom border (e.g. last item in a bordered panel).
   * Default true to match autocomplete list separators.
   */
  borderBottom?: boolean;
}

// Card art ~5:7 — match grid tiles so dropdown previews feel familiar.
const SUGGESTION_THUMB_W = 40;
const SUGGESTION_THUMB_H = Math.round((SUGGESTION_THUMB_W * 7) / 5);

/** Shared card suggestion row used by SearchAutocomplete and Did you mean. */
export const CardSearchSuggestionRow = memo(function CardSearchSuggestionRow({
  suggestion,
  isActive = false,
  onPointerEnter,
  onClick,
  borderBottom = true,
}: CardSearchSuggestionRowProps) {
  const meta = [suggestion.cardType, suggestion.region].filter(Boolean).join(' · ');
  const thumb =
    suggestion.thumbnailUrl != null && suggestion.thumbnailUrl !== ''
      ? typeaheadCardArtImgProps(suggestion.thumbnailUrl, SUGGESTION_THUMB_W)
      : null;

  return (
    <HStack
      px={3}
      py={2}
      gap={3}
      cursor={onClick ? 'pointer' : undefined}
      bg={isActive ? 'surface' : 'transparent'}
      _hover={onClick ? { bg: 'surface' } : undefined}
      onPointerEnter={onPointerEnter}
      onClick={onClick}
      borderBottomWidth={borderBottom ? 'hairline' : 0}
      borderColor="border"
      align="center"
      w="100%"
      textAlign="left"
    >
      <Box
        flexShrink={0}
        w={`${SUGGESTION_THUMB_W}px`}
        h={`${SUGGESTION_THUMB_H}px`}
        borderRadius="sm"
        overflow="hidden"
        bg="surface"
        border="hairline"
        borderColor="border"
      >
        {thumb ? (
          <Image
            src={thumb.src}
            {...(thumb.srcSet ? { srcSet: thumb.srcSet, sizes: thumb.sizes } : {})}
            alt={suggestion.name}
            w="100%"
            h="100%"
            objectFit="cover"
            loading="lazy"
            decoding="async"
            draggable={false}
          />
        ) : (
          <Flex w="100%" h="100%" align="center" justify="center">
            <Box w="16px" h="16px" borderRadius="sm" bg="border" />
          </Flex>
        )}
      </Box>

      <VStack align="flex-start" gap={0} flex={1} minW={0}>
        <Text
          fontSize="sm"
          fontWeight="medium"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          w="100%"
        >
          {suggestion.name}
        </Text>
        {meta && (
          <Text
            fontSize="xs"
            color="textMuted"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            w="100%"
          >
            {meta}
          </Text>
        )}
      </VStack>

      {suggestion.rarity && (
        <Text fontSize="xs" color="textMuted" flexShrink={0}>
          {suggestion.rarity}
        </Text>
      )}
    </HStack>
  );
});

export interface SearchSuggestionSectionLabelProps {
  children: string;
  /** Extra horizontal padding — autocomplete uses 3 to align with rows. */
  px?: number | string;
}

/** Muted section title used above suggestion groups (“Did you mean”, “Recent searches”). */
export function SearchSuggestionSectionLabel({
  children,
  px = 3,
}: SearchSuggestionSectionLabelProps) {
  return (
    <Text
      px={px}
      pt={2}
      pb={1}
      fontSize="xs"
      fontWeight="semibold"
      color="textMuted"
      userSelect="none"
    >
      {children}
    </Text>
  );
}
